import React, { useState, useEffect, useCallback } from 'react';
import { View, StyleSheet, FlatList, RefreshControl, Alert, Platform, Modal } from 'react-native';
import { Card, Text, Title, Button, ActivityIndicator, Divider, useTheme, Badge, IconButton, Snackbar } from 'react-native-paper';
import { useFocusEffect } from '@react-navigation/native';
import { getMyPendingActivities, cancelPendingActivity } from '../../api/activitiesAPI';
import { formatDateTime, formatTime, getElapsedTime } from '../../utils/dateUtils';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import EmptyState from '../../components/EmptyState';
import { savePaginationState, loadPaginationState } from '../../utils/paginationUtils';

const SCREEN_KEY = 'user_pending_activities_screen';

const PendingActivitiesScreen = ({ navigation }) => {
  const theme = useTheme();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  
  // Pagination state'ini localStorage'dan yükle
  const savedPagination = Platform.OS === 'web' ? 
    loadPaginationState(SCREEN_KEY, 1, 5) : 
    { currentPage: 1, itemsPerPage: 5 };
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(savedPagination.currentPage);
  const [itemsPerPage, setItemsPerPage] = useState(savedPagination.itemsPerPage);
  
  // Pagination state değiştiğinde localStorage'a kaydet (web için)
  useEffect(() => {
    if (Platform.OS === 'web') {
      savePaginationState(SCREEN_KEY, currentPage, itemsPerPage);
    }
  }, [currentPage, itemsPerPage]);

  // Onay bekleyen aktiviteleri yenile
  const fetchActivities = async () => {
    try {
      setError(null);
      const response = await getMyPendingActivities();

      if (response.success && response.data) {
        // Süre hesaplamalarını ekleyerek aktiviteleri işle
        const activitiesWithDuration = response.data.map(activity => ({
          ...activity,
          elapsed: getElapsedTime(activity.requested_time || activity.created_at)
        }));

        setActivities(activitiesWithDuration);
      } else {
        setError('Aktiviteler yüklenemedi');
      }
    } catch (err) {
      console.error('Aktiviteleri getirirken hata:', err);
      setError('Aktiviteleri yüklerken bir hata oluştu.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Süreyi her saniye güncelle
  useEffect(() => {
    if (activities.length > 0) {
      const interval = setInterval(() => {
        const updatedActivities = activities.map(activity => ({
          ...activity,
          elapsed: getElapsedTime(activity.requested_time || activity.created_at)
        }));
        setActivities(updatedActivities);
      }, 60000); // Her dakika güncelle

      return () => clearInterval(interval);
    }
  }, [activities]);

  // Sayfa odaklandığında aktiviteleri yükle
  useFocusEffect(
    useCallback(() => {
      fetchActivities();

      return () => {
        // Temizleme işlemleri (gerekirse)
      };
    }, [])
  );

  const onRefresh = () => {
    setRefreshing(true);
    fetchActivities();
    setCurrentPage(1); // Yenileme sırasında ilk sayfaya dön
  };

  // Pagination hesaplamaları
  const totalPages = Math.ceil(activities.length / itemsPerPage);
  
  // Mevcut sayfada gösterilecek aktiviteler
  const paginatedActivities = activities.slice(
    (currentPage - 1) * itemsPerPage, 
    currentPage * itemsPerPage
  );
  
  // Önceki sayfaya geç
  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };
  
  // Sonraki sayfaya geç
  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };
  
  // Sayfa numaralarını göster
  const renderPagination = () => {
    // Tüm durumlarda pagination'ı göster
    return (
      <View style={styles.paginationContainer}>
        <IconButton
          icon="chevron-left"
          size={24}
          onPress={goToPreviousPage}
          disabled={currentPage === 1}
          style={[styles.paginationButton, currentPage === 1 ? styles.paginationButtonDisabled : null]}
          iconColor="rgb(125, 76, 236)"
        />
        
        <Text style={styles.paginationText}>
          Sayfa {currentPage} / {Math.max(totalPages, 1)} ({activities.length} kayıt)
        </Text>
        
        <IconButton
          icon="chevron-right"
          size={24}
          onPress={goToNextPage}
          disabled={currentPage >= totalPages}
          style={[styles.paginationButton, currentPage >= totalPages ? styles.paginationButtonDisabled : null]}
          iconColor="rgb(125, 76, 236)"
        />
      </View>
    );
  };

  // Aktiviteyi iptal etme işlemini başlat
  const handleCancelActivity = (activity) => {
    setSelectedActivity(activity);
    setCancelDialogVisible(true);
  };
  
  // İptal işlemini onayla ve gerçekleştir
  const confirmCancelActivity = async () => {
    if (!selectedActivity) return;
    
    setCancelDialogVisible(false);
    setCancellingActivity(true);
    
    try {
      const response = await cancelPendingActivity(selectedActivity.id);
      
      if (response.success) {
        setSnackbarMessage('Ziyaret talebi başarıyla iptal edildi');
        // Listeyi yenile
        fetchActivities();
      } else {
        setSnackbarMessage('Ziyaret talebi iptal edilemedi: ' + response.message);
      }
    } catch (error) {
      console.error('Aktivite iptal hatası:', error);
      setSnackbarMessage('Ziyaret talebi iptal edilirken bir hata oluştu');
    } finally {
      setCancellingActivity(false);
      setSnackbarVisible(true);
    }
  };

  // İptal işlemi için state'ler
  const [cancelDialogVisible, setCancelDialogVisible] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [cancellingActivity, setCancellingActivity] = useState(false);
  const [snackbarVisible, setSnackbarVisible] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // Aktivite kartını render et
  const renderActivityItem = ({ item: activity }) => {
    const childName = activity.child ? 
      `${activity.child.first_name} ${activity.child.last_name}` : 
      'Bilinmeyen Çocuk';
    const activityTypeName = activity.activityType?.name || 'Bilinmeyen Aktivite';
    const requestedTime = formatDateTime(activity.requested_time || activity.created_at);

    return (
      <Card style={styles.activityCard}>
        <Card.Content>
          <View style={styles.activityHeader}>
            <View style={styles.nameContainer}>
              <Title style={styles.childName}>{childName}</Title>
              <Badge style={styles.pendingBadge}>Onay Bekliyor</Badge>
            </View>
            <Text style={styles.activityType}>{activityTypeName}</Text>
          </View>

          <Divider style={styles.divider} />

          <View style={styles.infoContainer}>
            <View style={styles.infoItem}>
              <MaterialCommunityIcons name="calendar-clock" size={20} color="rgb(125, 76, 236)" />
              <Text style={styles.infoText}>Talep: {formatDateTime(activity.requested_time || activity.created_at)}</Text>
            </View>

            <View style={styles.infoItem}>
              <MaterialCommunityIcons name="timer-outline" size={20} color="#FF9800" />
              <Text style={styles.infoText}>Bekleme Süresi: {activity.elapsed.display}</Text>
            </View>

            <View style={styles.infoItem}>
              <MaterialCommunityIcons name="alert-circle-outline" size={20} color="#FFC107" />
              <Text style={styles.infoText}>
                Personel onayı bekleniyor...
              </Text>
            </View>
          </View>

          <Text style={styles.noteText}>
            Lütfen personelden bu ziyareti onaylamasını isteyin.
          </Text>
          
          <Button 
            mode="contained" 
            icon="close-circle"
            labelStyle={{color: 'white'}}
            style={styles.cancelButton}
            onPress={() => handleCancelActivity(activity)}
          >
            İptal Et
          </Button>
        </Card.Content>
      </Card>
    );
  };

  return (
    <View style={styles.container}>
      {loading && !refreshing ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="rgb(125, 76, 236)" />
          <Text style={styles.loadingText}>Aktiviteler yükleniyor...</Text>
        </View>
      ) : (
        <>
          {activities.length === 0 ? (
            <EmptyState
              icon="clock-time-four-outline"
              message="Bekleyen ziyaret yok"
              description="Şu anda bekleyen bir park ziyareti talebiniz bulunmuyor."
              buttonLabel="Ziyaret Oluştur"
              buttonColor="rgb(125, 76, 236)"
              iconColor="rgb(125, 76, 236)"
              messageColor="rgb(125, 76, 236)"
              descriptionColor="rgb(90, 50, 180)"
              onPress={() => navigation.navigate('ParkEntry')}
            />
          ) : (
            <FlatList
              data={paginatedActivities}
              renderItem={renderActivityItem}
              keyExtractor={(item) => item.id.toString()}
              contentContainerStyle={styles.listContainer}
              refreshControl={
                <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
              }
              ListFooterComponent={renderPagination}
            />
          )}
        </>
      )}
      
      {/* İptal Onay Modal */}
      <Modal
        visible={cancelDialogVisible}
        transparent={true}
        onRequestClose={() => setCancelDialogVisible(false)}
      >
        <View style={styles.modalContainer}>
          <View style={styles.modalContent}>
            <MaterialCommunityIcons 
              name="alert-circle-outline" 
              size={50} 
              color="rgb(125, 76, 236)"
            /> 
            
            <Text style={styles.modalTitle}>Ziyaret Talebini İptal Et</Text>
            <Text style={styles.modalText}>
              Bu ziyaret talebini iptal etmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
            </Text>
            
            <View style={styles.modalButtonContainer}>
              <Button 
                mode="contained" 
                onPress={confirmCancelActivity}
                loading={cancellingActivity}
                disabled={cancellingActivity}
                style={[styles.modalButton, { backgroundColor: 'rgb(125, 76, 236)' }]}
                icon="close-circle"
              >
                İptal Et
              </Button>
              <Button 
                mode="outlined" 
                onPress={() => setCancelDialogVisible(false)}
                style={[styles.modalButton, { borderColor: 'rgb(125, 76, 236)' }]}
              >
                Vazgeç
              </Button>
            </View>
          </View>
        </View>
      </Modal>
      
      {/* Bildirim Snackbar */}
      <Snackbar
        visible={snackbarVisible}
        onDismiss={() => setSnackbarVisible(false)}
        duration={3000}
        style={{backgroundColor: 'rgba(125, 76, 236, 0.9)'}}
        action={{
          label: 'Tamam',
          onPress: () => setSnackbarVisible(false),
          labelStyle: {color: 'white'}
        }}
      >
        {snackbarMessage}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'rgb(232, 162, 249)',
  },
  modalContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  modalContent: {
    width: '80%',
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    borderRadius: 10,
    padding: 20,
    alignItems: 'center',
    elevation: 5,
    borderWidth: 1,
    borderColor: 'rgba(125, 76, 236, 0.3)',
  },
  modalIcon: {
    marginBottom: 15,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 10,
    color: 'rgb(125, 76, 236)',
  },
  modalText: {
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 20,
    color: 'rgb(90, 50, 180)',
  },
  modalButtonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    width: '100%',
  },
  modalButton: {
    marginHorizontal: 5,
    minWidth: 120,
  },
  scrollContent: {
    padding: 16,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 10,
    fontSize: 16,
    color: 'rgb(125, 76, 236)',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
    marginTop: 50,
  },
  errorText: {
    marginTop: 10,
    fontSize: 16,
    color: 'rgb(125, 76, 236)',
    textAlign: 'center',
    marginBottom: 20,
  },
  retryButton: {
    marginTop: 10,
    backgroundColor: 'rgb(125, 76, 236)',
  },
  activityCard: {
    marginBottom: 16,
    elevation: 2,
    backgroundColor: 'rgba(255, 255, 255, 0.85)',
    borderRadius: 8,
  },
  activityHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  nameContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  childName: {
    fontSize: 18,
    marginRight: 10,
    color: 'rgb(125, 76, 236)',
    fontWeight: 'bold',
  },
  pendingBadge: {
    backgroundColor: 'rgba(125, 76, 236, 0.6)',
    color: 'white',
  },
  activityType: {
    color: 'rgb(90, 50, 180)',
    fontSize: 14,
  },
  divider: {
    marginVertical: 12,
    backgroundColor: 'rgba(125, 76, 236, 0.2)',
  },
  infoContainer: {
    marginBottom: 16,
  },
  infoItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  infoText: {
    marginLeft: 8,
    fontSize: 14,
    color: 'rgb(90, 50, 180)',
  },
  noteText: {
    fontStyle: 'italic',
    color: 'rgb(90, 50, 180)',
    marginTop: 10,
    marginBottom: 16,
    fontSize: 14,
  },
  paginationContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 8,
    backgroundColor: 'rgba(255, 255, 255, 0.85)',
    elevation: 2,
    borderRadius: 8,
    marginHorizontal: 16,
    marginBottom: 16,
  },
  paginationButton: {
    margin: 0,
  },
  paginationButtonDisabled: {
    opacity: 0.5,
  },
  paginationText: {
    fontSize: 14,
    marginHorizontal: 12,
    color: 'rgb(125, 76, 236)',
    fontWeight: 'bold',
  },
  cancelButton: {
    marginTop: 8,
    backgroundColor: 'rgb(125, 76, 236)',
  },
});

export default PendingActivitiesScreen;
